import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { IonContent, ToastController } from '@ionic/angular';

import { UserService } from '../../services/user.service';
import { StripeService, StripePayment } from '../../services/stripe.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ENV_CONFIG } from '../../../bin/env.config';
import { Salon } from '../../models/salon/salon.model';
import { ModalController } from '@ionic/angular';
import { MailingAddressComponent } from '../../components/mailing-address/mailing-address.component';

@Component({
  selector: 'smr-payment-processing-settings',
  templateUrl: './payment-processing-settings.page.html',
  styleUrls: ['./payment-processing-settings.page.scss'],
})
export class PaymentProcessingSettingsPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  public isLoading: boolean;

  public discoveredReaders: Array<any> = [];

  public stripeUserId: string;

  public discoveringReader: boolean = false;

  public hasStripeAccount: boolean = false;

  public createdStripeAccount: boolean = false;

  public checkingCardReaders: boolean = true;

  public connectPushed: Array<boolean> = [];

  public disconnectPush: Array<boolean> = [];

  public cardReaderConnectError: Array<boolean> = [];

  public readerConnectSuccessful: Array<boolean> = [];

  public readerDisconnectSuccessful: Array<boolean> = [];

  public stripeAccountInfo: Array<StripePayment>;

  public errorOccurredWhileLoadingStats: boolean;
  public salon: Salon;

  constructor(
    private userService: UserService,
    private stripeService: StripeService,
    private ref: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private toastCtrl: ToastController,
    private modalCtrl: ModalController
  ) {
    this.salon = userService.getSalon().clone();
  }

  public async checkCardReaders() {
    this.disconnectPush = [];
    this.connectPushed = [];
    this.readerDisconnectSuccessful = [];
    this.readerConnectSuccessful = [];
    this.cardReaderConnectError = [];
    this.discoveringReader = false;
    const res = await this.stripeService.discoverReaders();

    if (res.code === 'reader_not_found' || res.code === 'reader_error') {
      this.cardReaderUnsuccessful(res);
      return;
    }
    this.discoveredReaders = await Promise.all(
      res.discoveredReaders.map(async (reader) => {
        this.disconnectPush.push(false);
        this.connectPushed.push(false);
        this.readerDisconnectSuccessful.push(false);
        this.readerConnectSuccessful.push(false);
        this.cardReaderConnectError.push(false);
        return { reader, status: await this.readerStatus(reader) };
      })
    );

    this.discoveringReader = true;
    this.ref.detectChanges();
  }
  /**
   * Attempts to connect a card reader to the system.
   * @param reader - The card reader object to connect.
   * @param i - The index of the reader in the array of discovered readers.
   */
  public async connectCardReader(reader, i) {
    // Set UI state to indicate connection attempt
    this.connectPushed[i] = true;
    this.cardReaderConnectError[i] = false;

    try {
      // Attempt to connect the reader
      const connectResponse = await this.stripeService.connectReader(reader);

      // Connection successful
      this.readerConnectSuccessful[i] = true;
      this.discoveredReaders[i].status = true;
    } catch (error) {
      // Connection failed
      this.cardReaderConnectError[i] = true;

      // Show an error message to the user
      const errorMessage = error.message || 'Failed to connect card reader';
      this.showErrorToast(errorMessage);
      throw new Error(`Error connecting card reader: ${error.message}`);
    } finally {
      // Update the view to reflect the connection status
      this.connectPushed[i] = false;
      this.ref.detectChanges();
    }
  }

  /**
   * Disconnects a specific card reader from the Stripe terminal.
   *
   * @param reader - The card reader object to disconnect.
   * @param i - The index of the reader in the array of discovered readers.
   * @returns A promise that resolves when the disconnection process is complete.
   */
  public async disconnectReader(reader: any, i: number): Promise<void> {
    // Set UI state to indicate disconnection attempt
    this.disconnectPush[i] = true;

    try {
      // Attempt to disconnect the reader using the Stripe service
      await this.stripeService.disconnectReader(reader);
      this.discoveredReaders[i].status = false;
      // Update UI to show successful disconnection
      this.readerDisconnectSuccessful[i] = true;
    } catch (error) {
      // Handle potential errors (e.g., log error, show user message)
      throw new Error(`Error disconnecting reader: ${error.message}`);
      // Optionally, update UI to show disconnection failure
      // this.readerDisconnectFailed[i] = true;
    } finally {
      // Ensure the view is updated regardless of success or failure
      this.disconnectPush[i] = false;
      this.ref.detectChanges();
    }
  }

  public async readerStatus(reader) {
    return (await this.stripeService.getStatus(reader)) === 'connected';
  }
  public cardReaderUnsuccessful(res) {
    if (res.code === 'reader_not_found') {
      this.discoveringReader = true;
      this.discoveredReaders = [];
    } else if (res.code === 'reader_error') {
      this.discoveringReader = true;
      this.discoveredReaders = [-1];
    }
  }
  public async ngOnInit() {
    if (
      this.salon.address1 == '' ||
      this.salon.address1 == undefined ||
      this.salon.city == '' ||
      this.salon.city == undefined ||
      this.salon.province == '' ||
      this.salon.province == undefined ||
      this.salon.postal == '' ||
      this.salon.postal == undefined ||
      this.salon.postal == '' ||
      this.salon.postal == undefined ||
      this.salon.country == undefined
    ) {
      const modal = await this.modalCtrl.create({
        component: MailingAddressComponent,
        backdropDismiss: false,
      });

      modal.present();
    }
    this.loadStripeData();
  }

  public loadStripeData() {
    this.isLoading = true;
    this.errorOccurredWhileLoadingStats = false;
    var code;
    var state;
    this.route.queryParams.subscribe((params) => {
      code = params['code'];
      state = params['state'];
      if (code && state) {
        this.router.navigate([], {
          queryParams: {
            code: null,
            state: null,
          },
          queryParamsHandling: 'merge',
        });
        // this is the data returned from the stripe connect oauth flow for setting up a new stripe account
        this.stripeService.insertStripeAccountInfo({ code, state }).subscribe({
          next: async (res) => {
            this.isLoading = false;
            this.hasStripeAccount = true;
            // reload payment settings page without query parameters
            this.ref.detectChanges();
            // right now
            const toast = await this.toastCtrl.create({
              message: `Congratulations your payment processing is now setup and connected to deposit funds into your account.`,
              duration: 3000,
            });
            toast.present();

            if (window && window['Intercom']) {
              const intercom = window['Intercom'];
              intercom('update', { company: { Has_stripe_account: true } });
            }
          },
          error: (err) => {
            this.isLoading = false;
            this.errorOccurredWhileLoadingStats = true;
            this.ref.detectChanges();
            this.loadStripeData();
          },
        });
      } else {
        // if they have a stripe account, load it's data
        this.stripeService.loadStripeAccountInfo().subscribe({
          next: async ({ stripeAccountInfo }) => {
            this.isLoading = false;
            this.stripeAccountInfo = stripeAccountInfo.toArray();
            this.hasStripeAccount = this.stripeAccountInfo.length !== 0;
            this.stripeUserId = this.hasStripeAccount
              ? this.stripeAccountInfo[0]['stripeUserID']
              : null;
            await this.checkCardReaders();
            this.ref.detectChanges();
          },
          error: (err) => {
            this.isLoading = false;
            this.errorOccurredWhileLoadingStats = true;
            this.ref.detectChanges();
          },
        });
      }
    });
  }

  public setupPaymentProcessing() {
    const userData = JSON.parse(localStorage.getItem('user'));
    const salonID = JSON.parse(localStorage.getItem('salonID'));
    const stripe_user: string = `stripe_user[email]=${userData['email']}&stripe_user[phone_number]=${userData['phone1']}&stripe_user[first_name]=${userData['firstName']}&stripe_user[last_name]=${userData['lastName']}&stripe_user[street_address]=${userData['address1']} ${userData['address2']}&stripe_user[zip]=${userData['postal']}`;
    window.location.href = `https://connect.stripe.com/express/oauth/authorize?response_type=code&client_id=${ENV_CONFIG.STRIPE_CLIENT_ID}&scope=read_write&state=${salonID}&${stripe_user}`; // testing
  }

  public viewAccountSettings() {
    this.stripeService
      .getViewAccountSettingsLink({ stripeUserId: this.stripeUserId })
      .subscribe((link) => {
        const url = link['url'];
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          newWindow.focus();
        } else {
          // Fallback for mobile browsers that block window.open
          window.location.href = url;
        }
      });
  }

  public onTapToReload() {
    this.loadStripeData();
  }

  public showCardReader() {
    this.router.navigateByUrl(
      '/settings/payment-processing-settings/connect-card-reader'
    );
  }

  public showChat() {
    if (window && window['Intercom']) {
      const intercom = window['Intercom'];
      intercom('show');
    }
  }

  public viewPayouts() {
    this.stripeService
      .fetchClientSecret({ accountID: this.stripeUserId })
      .subscribe((data) => {
        const stripeConnectInstance =
          this.stripeService.createStripeConnectInstance(data['client_secret']);
        const payoutComponent = stripeConnectInstance.create('payouts');
        const container = document.getElementById('container');
        if (container.hasChildNodes()) {
          container.replaceChild(payoutComponent, container.firstChild);
        } else {
          container.appendChild(payoutComponent);
        }
      });
  }

  public viewPayments() {
    this.stripeService
      .fetchClientSecret({ accountID: this.stripeUserId })
      .subscribe((data) => {
        const stripeConnectInstance =
          this.stripeService.createStripeConnectInstance(data['client_secret']);
        const paymentComponent = stripeConnectInstance.create('payments');
        const container = document.getElementById('container');
        if (container.hasChildNodes()) {
          container.replaceChild(paymentComponent, container.firstChild);
        } else {
          container.appendChild(paymentComponent);
        }
      });
  }

  // Add this method to show error messages to the user
  private async showErrorToast(message: string) {
    const toast = await this.toastCtrl.create({
      message: message,
      duration: 3000,
      color: 'danger',
    });
    toast.present();
  }

  public resetCardReader() {
    localStorage.removeItem('pos-posdeviceid');
    localStorage.removeItem('cardReader');
    window.location.reload();
  }
}
